// src/Admin/AdminSupport/AdminSupport.js
// UPDATED: Added conversation thread view and direct reply functionality

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { 
    collection, query, orderBy, onSnapshot, doc, updateDoc, arrayUnion, Timestamp, serverTimestamp
} from 'firebase/firestore';
import { Icon } from '@iconify/react';
import './AdminStyles.css';

const AdminSupport = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, open, in_progress, resolved
    
    // Selected ticket for conversation view
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);

    // Fetch ALL tickets
    useEffect(() => {
        const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            const ticketData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setTickets(ticketData);
            setLoading(false);
            
            // Update selected ticket if open
            if (selectedTicket) {
                const updated = ticketData.find(t => t.id === selectedTicket.id);
                if (updated) setSelectedTicket(updated);
            }
        });
        return () => unsub();
    }, [selectedTicket?.id]);

    // Filter tickets
    const filteredTickets = tickets.filter(t => {
        if (filter === 'all') return true;
        return t.status === filter;
    });

    // Send Reply
    const handleSendReply = async () => {
        if (!replyText.trim() || !selectedTicket) return;

        setSending(true);
        try {
            const ticketRef = doc(db, 'tickets', selectedTicket.id);
            await updateDoc(ticketRef, {
                messages: arrayUnion({
                    from: 'admin',
                    name: 'AiPredict Team',
                    text: replyText.trim(),
                    timestamp: Timestamp.now()
                }),
                updatedAt: serverTimestamp(),
                unreadByUser: true,  // User will see notification
                unreadByAdmin: false,
                // Auto-set to in_progress if was open
                status: selectedTicket.status === 'open' ? 'in_progress' : selectedTicket.status
            });
            setReplyText('');
        } catch (err) {
            console.error("Reply Error:", err);
            alert("Failed to send reply.");
        } finally {
            setSending(false);
        }
    };

    // Update Status
    const updateStatus = async (ticketId, newStatus) => {
        try {
            await updateDoc(doc(db, 'tickets', ticketId), { 
                status: newStatus,
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            alert("Error updating status.");
        }
    };

    // Mark as read by admin
    const handleSelectTicket = async (ticket) => {
        setSelectedTicket(ticket);
        
        if (ticket.unreadByAdmin) {
            try {
                await updateDoc(doc(db, 'tickets', ticket.id), {
                    unreadByAdmin: false
                });
            } catch (err) {
                console.error("Mark read error:", err);
            }
        }
    };

    // Format timestamp
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString([], { 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    // Status styles
    const getStatusStyle = (status) => {
        switch (status) {
            case 'open':
                return { background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' };
            case 'in_progress':
                return { background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' };
            case 'resolved':
                return { background: 'rgba(74, 222, 128, 0.15)', color: '#4ade80' };
            default:
                return { background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8' };
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'in_progress': return 'In Progress';
            case 'resolved': return 'Resolved';
            default: return 'Open';
        }
    };

    // Stats
    const openCount = tickets.filter(t => t.status === 'open').length;
    const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
    const unreadCount = tickets.filter(t => t.unreadByAdmin).length;

    return (
        <div className="admin-module" style={{ 
            padding: '20px',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            {/* HEADER */}
            <header className="admin-header" style={{ marginBottom: '20px' }}>
                <div>
                    <h1 className="admin-title">
                        <Icon icon="solar:headphones-round-sound-bold" style={{ marginRight: '10px', color: '#60a5fa' }} />
                        Support Command
                    </h1>
                    <p className="admin-subtitle">Manage user tickets and respond directly in-app.</p>
                </div>
                <div className="admin-stats-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {unreadCount > 0 && (
                        <span className="admin-badge" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#f87171' }}>
                            <Icon icon="solar:bell-bold" /> {unreadCount} Unread
                        </span>
                    )}
                    <span className="admin-badge" style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' }}>
                        {openCount} Open
                    </span>
                    <span className="admin-badge" style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa' }}>
                        {inProgressCount} In Progress
                    </span>
                </div>
            </header>

            {/* FILTER TABS */}
            <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '20px',
                flexWrap: 'wrap'
            }}>
                {['all', 'open', 'in_progress', 'resolved'].map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: filter === f ? '1px solid #3b82f6' : '1px solid #334155',
                            background: filter === f ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                            color: filter === f ? '#60a5fa' : '#94a3b8',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: filter === f ? '600' : '400',
                            transition: 'all 0.2s'
                        }}
                    >
                        {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                        {f !== 'all' && (
                            <span style={{ marginLeft: '6px', opacity: 0.7 }}>
                                ({tickets.filter(t => t.status === f).length})
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {loading ? (
                <p style={{ color: '#94a3b8' }}>
                    <Icon icon="solar:spinner-bold" style={{ animation: 'spin 1s linear infinite' }} /> Scanning...
                </p>
            ) : (
                <div className="admin-support-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: selectedTicket ? 'minmax(300px, 400px) 1fr' : '1fr',
                    gap: '20px',
                    maxWidth: '100%',
                    overflow: 'hidden'
                }}>
                    {/* LEFT: Ticket List */}
                    <div style={{
                        maxHeight: '700px',
                        overflowY: 'auto',
                        minWidth: 0
                    }}>
                        {filteredTickets.length === 0 && (
                            <div style={{
                                background: 'rgba(30, 41, 59, 0.5)',
                                padding: '40px',
                                borderRadius: '12px',
                                textAlign: 'center'
                            }}>
                                <Icon icon="solar:inbox-bold" style={{ fontSize: '2.5rem', color: '#475569', marginBottom: '12px' }} />
                                <p style={{ color: '#64748b' }}>No tickets found.</p>
                            </div>
                        )}

                        {filteredTickets.map(ticket => (
                            <div 
                                key={ticket.id} 
                                onClick={() => handleSelectTicket(ticket)}
                                className="admin-card" 
                                style={{ 
                                    opacity: ticket.status === 'resolved' ? 0.7 : 1,
                                    marginBottom: '12px',
                                    cursor: 'pointer',
                                    border: selectedTicket?.id === ticket.id 
                                        ? '1px solid #3b82f6' 
                                        : '1px solid rgba(71, 85, 105, 0.3)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div className="admin-card-header" style={{ marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <h3 className="admin-card-title" style={{ margin: 0, fontSize: '1rem' }}>
                                                {ticket.subject}
                                            </h3>
                                            {ticket.unreadByAdmin && (
                                                <span style={{
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    background: '#ef4444',
                                                    display: 'inline-block'
                                                }} title="Unread" />
                                            )}
                                        </div>
                                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                            {ticket.email}
                                        </span>
                                    </div>
                                    <span style={{ 
                                        padding: '4px 10px', 
                                        borderRadius: '12px', 
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        ...getStatusStyle(ticket.status)
                                    }}>
                                        {formatStatus(ticket.status)}
                                    </span>
                                </div>

                                <p style={{ 
                                    color: '#94a3b8', 
                                    fontSize: '0.85rem',
                                    margin: '8px 0',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {ticket.messages?.[ticket.messages.length - 1]?.text || ticket.message || 'No message'}
                                </p>

                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    fontSize: '0.75rem',
                                    color: '#475569'
                                }}>
                                    <span>{ticket.messages?.length || 1} message{(ticket.messages?.length || 1) !== 1 ? 's' : ''}</span>
                                    <span>{formatTime(ticket.updatedAt || ticket.createdAt)}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RIGHT: Conversation View */}
                    {selectedTicket && (
                        <div style={{
                            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.9))',
                            borderRadius: '16px',
                            border: '1px solid rgba(71, 85, 105, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '700px',
                            minWidth: 0,
                            overflow: 'hidden'
                        }}>
                            {/* Header */}
                            <div style={{
                                padding: '16px 20px',
                                borderBottom: '1px solid rgba(71, 85, 105, 0.3)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start'
                            }}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ margin: '0 0 4px 0', color: '#e2e8f0' }}>
                                        {selectedTicket.subject}
                                    </h3>
                                    <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#64748b' }}>
                                        <strong>{selectedTicket.displayName}</strong> • {selectedTicket.email}
                                    </p>
                                    
                                    {/* Status Dropdown */}
                                    <select
                                        value={selectedTicket.status}
                                        onChange={(e) => updateStatus(selectedTicket.id, e.target.value)}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #334155',
                                            background: '#0f172a',
                                            color: '#e2e8f0',
                                            fontSize: '0.85rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="open">🟡 Open</option>
                                        <option value="in_progress">🔵 In Progress</option>
                                        <option value="resolved">🟢 Resolved</option>
                                    </select>
                                </div>
                                
                                <button 
                                    onClick={() => setSelectedTicket(null)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#64748b',
                                        cursor: 'pointer',
                                        padding: '8px'
                                    }}
                                >
                                    <Icon icon="solar:close-circle-bold" style={{ fontSize: '1.5rem' }} />
                                </button>
                            </div>

                            {/* Messages Thread */}
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '16px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px'
                            }}>
                                {/* Support for old single-message format */}
                                {!selectedTicket.messages && selectedTicket.message && (
                                    <div style={{ alignSelf: 'flex-start' }}>
                                        <div style={{
                                            maxWidth: '85%',
                                            padding: '12px 16px',
                                            borderRadius: '16px 16px 16px 4px',
                                            background: 'rgba(71, 85, 105, 0.3)',
                                            color: '#e2e8f0'
                                        }}>
                                            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                                                {selectedTicket.message}
                                            </p>
                                        </div>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px', 
                                            marginTop: '4px',
                                            color: '#64748b',
                                            fontSize: '0.75rem'
                                        }}>
                                            <span style={{ fontWeight: '600' }}>{selectedTicket.displayName}</span>
                                            <span>•</span>
                                            <span>{formatTime(selectedTicket.createdAt)}</span>
                                        </div>
                                    </div>
                                )}

                                {/* New messages array format */}
                                {selectedTicket.messages?.map((msg, idx) => (
                                    <div 
                                        key={idx}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: msg.from === 'admin' ? 'flex-end' : 'flex-start'
                                        }}
                                    >
                                        <div style={{
                                            maxWidth: '85%',
                                            padding: '12px 16px',
                                            borderRadius: msg.from === 'admin' 
                                                ? '16px 16px 4px 16px' 
                                                : '16px 16px 16px 4px',
                                            background: msg.from === 'admin'
                                                ? 'linear-gradient(135deg, #10b981, #059669)'
                                                : 'rgba(71, 85, 105, 0.3)',
                                            color: '#fff'
                                        }}>
                                            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                                                {msg.text}
                                            </p>
                                        </div>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px', 
                                            marginTop: '4px',
                                            color: '#64748b',
                                            fontSize: '0.75rem',
                                            padding: '0 4px'
                                        }}>
                                            <span style={{ 
                                                fontWeight: '600',
                                                color: msg.from === 'admin' ? '#4ade80' : '#94a3b8'
                                            }}>
                                                {msg.from === 'admin' ? 'You' : selectedTicket.displayName}
                                            </span>
                                            <span>•</span>
                                            <span>{formatTime(msg.timestamp)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Reply Input */}
                            <div style={{
                                padding: '16px 20px',
                                borderTop: '1px solid rgba(71, 85, 105, 0.3)',
                                background: 'rgba(15, 23, 42, 0.5)'
                            }}>
                                <div style={{ marginBottom: '10px' }}>
                                    <textarea
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Type your reply to the user..."
                                        rows="3"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: '10px',
                                            background: 'rgba(15, 23, 42, 0.8)',
                                            border: '1px solid rgba(71, 85, 105, 0.5)',
                                            color: '#fff',
                                            fontSize: '0.95rem',
                                            resize: 'vertical',
                                            minHeight: '80px'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {/* Quick Replies */}
                                        <button
                                            onClick={() => setReplyText("Thank you for reaching out! We're looking into this and will get back to you shortly.")}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid #334155',
                                                background: 'transparent',
                                                color: '#64748b',
                                                fontSize: '0.75rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            👋 Acknowledge
                                        </button>
                                        <button
                                            onClick={() => setReplyText("This issue has been resolved. Please let us know if you need any further assistance!")}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid #334155',
                                                background: 'transparent',
                                                color: '#64748b',
                                                fontSize: '0.75rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ✅ Resolved
                                        </button>
                                    </div>
                                    
                                    <button
                                        onClick={handleSendReply}
                                        disabled={sending || !replyText.trim()}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '10px',
                                            background: sending || !replyText.trim() 
                                                ? '#475569' 
                                                : 'linear-gradient(135deg, #10b981, #059669)',
                                            border: 'none',
                                            color: '#fff',
                                            cursor: sending || !replyText.trim() ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontWeight: '600',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        <Icon icon={sending ? "solar:spinner-bold" : "solar:plain-bold"} 
                                            style={{ animation: sending ? 'spin 1s linear infinite' : 'none' }} />
                                        {sending ? 'Sending...' : 'Send Reply'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .admin-module {
                    width: 100%;
                    max-width: 100%;
                    box-sizing: border-box;
                }
                
                @media (max-width: 1024px) {
                    .admin-support-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminSupport;